import React, { useEffect, useRef, useState } from 'react';
import { btnGhost, btnPrimary, card, input, label, overlay } from './styles';

const clamp01 = (v) => Math.max(0, Math.min(1, v));
const MIN_SIZE = 0.02; // fraction of the page — anything smaller is a sliver, the backend refuses it too
const NUDGE = 0.005;

const normalize = (b) => b && ({
  x1: Math.min(b.x1, b.x2), y1: Math.min(b.y1, b.y2),
  x2: Math.max(b.x1, b.x2), y2: Math.max(b.y1, b.y2),
});

/**
 * Draw or adjust a crop over a rendered catalog page. Drag on empty space to draw a new box,
 * drag inside the box to move it, drag a corner to resize. Arrow keys nudge the box
 * (Shift+arrows grow/shrink it) so it can be fine-tuned without a mouse.
 *
 * Boxes are fractions (0..1) of the page, the same shape the backend stores.
 */
export default function CropEditor({ pages, initial, title, onSave, onClose }) {
  const [pageNumber, setPageNumber] = useState(initial?.pageNumber ?? pages[0]?.pageNumber);
  const [box, setBox] = useState(initial ? { x1: initial.x1, y1: initial.y1, x2: initial.x2, y2: initial.y2 } : null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const surfaceRef = useRef(null);
  const drag = useRef(null);

  const page = pages.find((p) => p.pageNumber === pageNumber);
  const norm = normalize(box);
  const valid = !!norm && norm.x2 - norm.x1 >= MIN_SIZE && norm.y2 - norm.y1 >= MIN_SIZE;

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape' && !saving) onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose, saving]);

  const toFrac = (e) => {
    const r = surfaceRef.current.getBoundingClientRect();
    return { x: clamp01((e.clientX - r.left) / r.width), y: clamp01((e.clientY - r.top) / r.height) };
  };

  const startDrag = (e, mode) => {
    if (e.button !== undefined && e.button !== 0) return;
    e.preventDefault();
    e.stopPropagation();
    surfaceRef.current.setPointerCapture(e.pointerId);
    const start = toFrac(e);
    drag.current = { mode, start, orig: norm };
    if (mode === 'draw') setBox({ x1: start.x, y1: start.y, x2: start.x, y2: start.y });
  };

  const onPointerMove = (e) => {
    const d = drag.current;
    if (!d) return;
    const p = toFrac(e);
    const o = d.orig;
    switch (d.mode) {
      case 'draw': setBox({ x1: d.start.x, y1: d.start.y, x2: p.x, y2: p.y }); break;
      case 'move': {
        const dx = Math.max(-o.x1, Math.min(1 - o.x2, p.x - d.start.x));
        const dy = Math.max(-o.y1, Math.min(1 - o.y2, p.y - d.start.y));
        setBox({ x1: o.x1 + dx, y1: o.y1 + dy, x2: o.x2 + dx, y2: o.y2 + dy });
        break;
      }
      case 'nw': setBox({ x1: p.x, y1: p.y, x2: o.x2, y2: o.y2 }); break;
      case 'ne': setBox({ x1: o.x1, y1: p.y, x2: p.x, y2: o.y2 }); break;
      case 'sw': setBox({ x1: p.x, y1: o.y1, x2: o.x2, y2: p.y }); break;
      case 'se': setBox({ x1: o.x1, y1: o.y1, x2: p.x, y2: p.y }); break;
      default: break;
    }
  };

  const onPointerUp = () => {
    drag.current = null;
    setBox((b) => normalize(b));
  };

  const onSurfaceKey = (e) => {
    if (!norm) return;
    const dirs = { ArrowLeft: [-1, 0], ArrowRight: [1, 0], ArrowUp: [0, -1], ArrowDown: [0, 1] };
    const dir = dirs[e.key];
    if (!dir) return;
    e.preventDefault();
    const [dx, dy] = dir.map((v) => v * NUDGE);
    if (e.shiftKey) {
      setBox({ ...norm, x2: clamp01(norm.x2 + dx), y2: clamp01(norm.y2 + dy) });
    } else {
      const mx = Math.max(-norm.x1, Math.min(1 - norm.x2, dx));
      const my = Math.max(-norm.y1, Math.min(1 - norm.y2, dy));
      setBox({ x1: norm.x1 + mx, y1: norm.y1 + my, x2: norm.x2 + mx, y2: norm.y2 + my });
    }
  };

  const changePage = (n) => {
    setPageNumber(n);
    setBox(initial && initial.pageNumber === n ? { x1: initial.x1, y1: initial.y1, x2: initial.x2, y2: initial.y2 } : null);
  };

  const handleSave = async () => {
    if (!valid) return;
    setSaving(true);
    setError('');
    try {
      await onSave({ pageNumber, ...norm });
    } catch (err) {
      setError(err?.message || 'Could not save the crop.');
      setSaving(false);
    }
  };

  const handle = (corner, style) => (
    <div
      onPointerDown={(e) => startDrag(e, corner)}
      aria-hidden="true"
      style={{ position: 'absolute', width: 14, height: 14, background: '#fff', border: '2px solid #F29213', borderRadius: 3, boxSizing: 'border-box', cursor: `${corner}-resize`, touchAction: 'none', ...style }}
    />
  );

  return (
    <div style={overlay} role="dialog" aria-modal="true" aria-label={title}>
      <div style={{ ...card, width: 'min(1100px, 100%)', maxHeight: '94vh', overflowY: 'auto', marginBottom: 0 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', gap: 12, flexWrap: 'wrap', marginBottom: 12 }}>
          <div>
            <h3 style={{ margin: 0, fontSize: 17, fontWeight: 700 }}>{title}</h3>
            <p style={{ margin: '4px 0 0', fontSize: 12, color: '#6B7280' }}>
              Drag to draw a box around the product photo. Drag the box to move it, or a corner to resize. Arrow keys nudge it.
            </p>
          </div>
          {pages.length > 1 && (
            <div style={{ width: 130 }}>
              <label style={label} htmlFor="crop-page">Page</label>
              <select id="crop-page" style={input} value={pageNumber} onChange={(e) => changePage(Number(e.target.value))}>
                {pages.map((p) => <option key={p.pageNumber} value={p.pageNumber}>Page {p.pageNumber}</option>)}
              </select>
            </div>
          )}
        </div>

        {error && <div style={{ color: '#B91C1C', fontSize: 13, marginBottom: 10 }}>{error}</div>}

        <div style={{ display: 'flex', justifyContent: 'center', background: '#F3F4F6', borderRadius: 8, padding: 8 }}>
          <div
            ref={surfaceRef}
            tabIndex={0}
            aria-label="Crop area. Arrow keys move the box, Shift plus arrow keys resize it."
            onPointerDown={(e) => startDrag(e, 'draw')}
            onPointerMove={onPointerMove}
            onPointerUp={onPointerUp}
            onPointerCancel={onPointerUp}
            onKeyDown={onSurfaceKey}
            style={{ position: 'relative', display: 'inline-block', cursor: 'crosshair', overflow: 'hidden', touchAction: 'none', userSelect: 'none', outline: 'none' }}
          >
            {page?.renderUrl ? (
              <img src={page.renderUrl} alt={`Catalog page ${pageNumber}`} draggable={false}
                   style={{ display: 'block', maxWidth: '100%', maxHeight: '68vh' }} />
            ) : (
              <div style={{ padding: 40, color: '#9CA3AF' }}>Page image unavailable.</div>
            )}
            {norm && (
              <div
                onPointerDown={(e) => startDrag(e, 'move')}
                style={{
                  position: 'absolute', cursor: 'move', touchAction: 'none',
                  left: `${norm.x1 * 100}%`, top: `${norm.y1 * 100}%`,
                  width: `${(norm.x2 - norm.x1) * 100}%`, height: `${(norm.y2 - norm.y1) * 100}%`,
                  border: '2px solid #F29213', boxSizing: 'border-box',
                  boxShadow: '0 0 0 9999px rgba(17,24,39,0.45)',
                }}
              >
                {handle('nw', { left: -7, top: -7 })}
                {handle('ne', { right: -7, top: -7 })}
                {handle('sw', { left: -7, bottom: -7 })}
                {handle('se', { right: -7, bottom: -7 })}
              </div>
            )}
          </div>
        </div>

        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 14 }}>
          <button style={btnGhost} onClick={onClose} disabled={saving}>Cancel</button>
          <button style={{ ...btnPrimary, opacity: !valid || saving ? 0.6 : 1 }} onClick={handleSave} disabled={!valid || saving}>
            {saving ? 'Cutting…' : 'Save crop'}
          </button>
        </div>
      </div>
    </div>
  );
}
